/* <bb-hero> — Berry & Bump homepage hero: headline band + four diagonal flavour panels.
   Attributes: sources (JSON {id:{bar,ing}}), headline. Self-contained (shadow DOM). */
(function () {
  const FLAVOURS = [
    { id: 'coconut-glow', name: 'Coconut Glow', bg: '#E8C4C0' },
    { id: 'choconut-goldie', name: 'Choconut Goldie', bg: '#DCC4A5' },
    { id: 'midnight-brownie', name: 'Midnight Brownie', bg: '#B49E90' },
    { id: 'berry-boost', name: 'Berry Boost', bg: '#D4A8B6' }
  ];
  const TAN = Math.tan(25 * Math.PI / 180);
  const BAR_R = 527 / 953; // bar png h/w
  const EASE = 'cubic-bezier(.4,0,.2,1)';

  class BBHero extends HTMLElement {
    connectedCallback() {
      if (this._built) return; this._built = true;
      const root = this.attachShadow({ mode: 'open' });
      const srcs = JSON.parse(this.getAttribute('sources') || '{}');
      const headline = this.getAttribute('headline') || 'THE ONLY THING ON YOUR NIGHTSTAND...';
      root.innerHTML = `
<style>
  :host{display:block;background:#F6F0E3;color:#221D18;font-family:Montserrat,sans-serif}
  .band{text-align:center;padding:48px 24px 40px;border-bottom:1px solid rgba(34,29,24,.18)}
  h1{margin:0;font-family:Fraunces,serif;font-weight:400;font-size:clamp(24px,3.3vw,44px);letter-spacing:.12em;line-height:1.2;text-wrap:balance}
  .stage{position:relative;overflow:hidden}
  .panel{position:absolute;left:0;top:0;width:100%;height:100%;transition:transform 420ms ${EASE}}
  .wipe{position:absolute;inset:-10% -60%;background:#F6F0E3;transform:skewX(-25deg) translateX(0);transition:transform 900ms ${EASE};z-index:5;pointer-events:none}
  .in .wipe{transform:skewX(-25deg) translateX(110%)}
  .name{position:absolute;font-family:Fraunces,serif;font-size:13px;letter-spacing:.24em;text-transform:uppercase;white-space:nowrap;opacity:0;transition:opacity 700ms ${EASE}}
  .in .name{opacity:1}
  img{position:absolute;display:block;filter:drop-shadow(0 16px 20px rgba(60,40,28,.28));user-select:none;-webkit-user-drag:none;pointer-events:none}
  .bar{transform:translateX(-70px);opacity:0;transition:transform 900ms ${EASE},opacity 600ms ${EASE}}
  .in .bar{transform:translateX(0);opacity:1}
  .ing{transform:translateY(20px);opacity:0;transition:transform 900ms ${EASE},opacity 700ms ${EASE}}
  .in .ing{transform:translateY(0);opacity:1}
  .panel.lift{transform:translateY(-6px)}
  .panel.lift .bar{transform:translateX(var(--slide,60px))}
  .edge{position:absolute;top:-10%;height:120%;width:72px;transform:skewX(-25deg);transform-origin:top left;background:linear-gradient(to right,rgba(52,34,22,.18),rgba(52,34,22,.07) 45%,rgba(52,34,22,0));pointer-events:none;z-index:2}
  .bar{z-index:3}.ing{z-index:3}.name{z-index:4}
  @media (prefers-reduced-motion:reduce){
    .wipe{display:none}
    .bar,.ing,.name{transform:none!important;transition:opacity 600ms ${EASE}}
  }
</style>
<div class="band"><h1>${headline}</h1></div>
<div class="stage" part="stage">
  ${FLAVOURS.map((f, i) => `
  <div class="panel" data-i="${i}" style="background:${f.bg}">
    <div class="wipe" style="transition-delay:${i * 120}ms"></div>
    <div class="edge"></div>
    <div class="name" style="transition-delay:${i * 120 + 300}ms">${f.name}</div>
    <img class="bar" style="transition-delay:${i * 120 + 120}ms" src="${(srcs[f.id] || {}).bar || ''}" alt="${f.name} bar, wrapper torn open">
    <img class="ing" style="transition-delay:${i * 120 + 320}ms" src="${(srcs[f.id] || {}).ing || ''}" alt="${f.name} ingredients">
  </div>`).join('')}
</div>`;
      this._stage = root.querySelector('.stage');
      this._panels = Array.from(root.querySelectorAll('.panel'));
      this._ro = new ResizeObserver(() => this._layout());
      this._ro.observe(this);
      this._layout();
      const io = new IntersectionObserver((en) => { if (en.some(e => e.isIntersecting)) { this._stage.classList.add('in'); io.disconnect(); } }, { threshold: 0.2 });
      io.observe(this._stage);
      this._panels.forEach(p => {
        p.addEventListener('mouseenter', () => p.classList.add('lift'));
        p.addEventListener('mouseleave', () => p.classList.remove('lift'));
        p.addEventListener('click', () => p.classList.toggle('lift'));
      });
    }
    _layout() {
      const W = this.clientWidth; if (!W) return;
      const mobile = W < 760;
      const st = this._stage;
      if (!mobile) {
        const H = Math.round(Math.min(680, Math.max(480, W * 0.5)));
        const colW = W / 4, dx = H * TAN;
        const barW = colW * 1.18, barH = barW * BAR_R, step = H * 0.06;
        const HIDE = 0.42; // fraction of bar tucked beyond the diagonal
        const ingW = colW * 0.42;
        st.style.height = H + 'px';
        // diagonal x for panel i at height y (top = +dx/2, bottom = -dx/2)
        const diag = (i, y) => i * colW + dx / 2 - (y / H) * dx;
        this._panels.forEach((p, i) => {
          const L0 = i === 0 ? -dx : diag(i, 0), L1 = i === 0 ? -dx : diag(i, H), R0 = diag(i + 1, 0), R1 = diag(i + 1, H);
          const right0 = i === 3 ? W + dx : R0, right1 = i === 3 ? W + dx : R1;
          p.style.clipPath = `polygon(${L0}px 0, ${right0}px 0, ${right1}px ${H}px, ${L1}px ${H}px)`;
          const edge = p.querySelector('.edge');
          edge.style.display = i === 0 ? 'none' : 'block';
          edge.style.left = (diag(i, 0) + TAN * H * 0.1) + 'px';
          p.style.setProperty('--slide', barW * 0.16 + 'px');
          const nameY = 44;
          const name = p.querySelector('.name');
          const nameL = i === 0 ? 0 : diag(i, nameY), nameR = i === 3 ? W : diag(i + 1, nameY);
          name.style.top = nameY + 'px'; name.style.left = ((nameL + nameR) / 2) + 'px'; name.style.transform = 'translateX(-50%)';
          const barCY = H * 0.36 + i * step;
          const bar = p.querySelector('.bar');
          bar.style.width = barW + 'px'; bar.style.top = (barCY - barH / 2) + 'px'; bar.style.clipPath = '';
          bar.style.left = (diag(i, barCY) - barW * HIDE) + 'px';
          const ing = p.querySelector('.ing');
          const ingH = H * 0.26, ingY = H - ingH - 36;
          const yMid = ingY + ingH / 2;
          const cL = i === 0 ? 0 : diag(i, yMid), cR = i === 3 ? W : diag(i + 1, yMid);
          const minL = (i === 0 ? 0 : diag(i, ingY)) + 12; // top-left corner is the tightest point against the left diagonal
          ing.style.width = ingW + 'px'; ing.style.height = ingH + 'px'; ing.style.objectFit = 'contain'; ing.style.objectPosition = 'center bottom';
          ing.style.top = ingY + 'px'; ing.style.left = Math.max(minL, (cL + cR) / 2 - ingW / 2) + 'px';
        });
      } else {
        const Hb = Math.round(Math.max(180, Math.min(240, W * 0.55)));
        const dx = Hb * TAN;
        const barW = Math.min(W * 0.62, 300), barH = barW * BAR_R;
        const x0 = W * 0.06; // diagonal bottom x
        st.style.height = Hb * 4 + 'px';
        const diag = (y) => x0 + dx - (y / Hb) * dx; // within band
        const barCYm = Hb * 0.58, HIDE = 0.42;
        this._panels.forEach((p, i) => {
          const top = i * Hb;
          p.style.clipPath = `polygon(${x0 + dx}px ${top}px, ${W}px ${top}px, ${W}px ${top + Hb}px, ${x0}px ${top + Hb}px)`;
          const edge = p.querySelector('.edge');
          edge.style.display = 'block'; edge.style.top = top + 'px'; edge.style.height = Hb + 'px'; edge.style.left = (x0 + dx) + 'px';
          p.style.setProperty('--slide', barW * 0.14 + 'px');
          const name = p.querySelector('.name');
          name.style.top = (top + 18) + 'px'; name.style.left = (diag(18) + 16) + 'px'; name.style.transform = 'none';
          const barCY = barCYm;
          const bar = p.querySelector('.bar');
          bar.style.width = barW + 'px'; bar.style.top = (top + barCY - barH / 2) + 'px'; bar.style.clipPath = '';
          bar.style.left = (diag(barCY) - barW * HIDE) + 'px';
          const visRight = diag(barCY) + barW * (1 - HIDE);
          const ing = p.querySelector('.ing');
          const ingLeft = visRight + 14, ingW = Math.min(W - ingLeft - 16, 110);
          ing.style.width = ingW + 'px'; ing.style.height = (Hb * 0.4) + 'px'; ing.style.objectFit = 'contain'; ing.style.objectPosition = 'left bottom';
          ing.style.top = (top + Hb - Hb * 0.4 - 16) + 'px'; ing.style.left = ingLeft + 'px';
        });
      }
    }
    disconnectedCallback() { if (this._ro) this._ro.disconnect(); }
  }
  if (!customElements.get('bb-hero')) customElements.define('bb-hero', BBHero);
})();
